import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  collection,
  doc,
  updateDoc,
  arrayUnion,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../components/Auth/firebase";
import { Link } from "react-router-dom";
import Column from "../components/Tasks/Board/Column";
import Modal from "../components/Tasks/Board/Modal";
import AddTask from "../screens/AddTask";
import { Add } from "../components/Tasks/Board/Icons";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectsContext";
import useKanbanData from "../hooks/useKanbanData";
import { debounce } from "../utils/Boardutils";

const Kanban = () => {
  const { boardId } = useParams();
  const { currentUser } = useAuth();
  const { activeProject } = useProjects();
  const userId = currentUser?.uid;
  const projectId = activeProject?.id; // Ensure projectId is defined
  const { initialData, setInitialData, boardName } = useKanbanData(
    projectId,
    boardId
  );
  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState(null);
  const filters = ["high", "medium", "low"];

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;

    const newInitialData = { ...initialData };

    if (result.type === "task") {
      const startColumn = newInitialData.columns[source.droppableId];
      const endColumn = newInitialData.columns[destination.droppableId];

      if (startColumn === endColumn) {
        const newTaskIds = Array.from(endColumn.taskIds);
        newTaskIds.splice(source.index, 1);
        newTaskIds.splice(destination.index, 0, draggableId);
        newInitialData.columns[destination.droppableId].taskIds = newTaskIds;
      } else {
        const startTaskIDs = Array.from(startColumn.taskIds);
        startTaskIDs.splice(source.index, 1);
        const finishTaskIDs = Array.from(endColumn.taskIds);
        finishTaskIDs.splice(destination.index, 0, draggableId);
        newInitialData.columns[source.droppableId].taskIds = startTaskIDs;
        newInitialData.columns[destination.droppableId].taskIds = finishTaskIDs;
      }
    } else {
      const newColumnOrder = Array.from(newInitialData.columnOrder);
      newColumnOrder.splice(source.index, 1);
      newColumnOrder.splice(destination.index, 0, draggableId);
      newInitialData.columnOrder = newColumnOrder;
    }

    setInitialData(newInitialData);

    try {
      if (result.type === "task") {
        const startColumnRef = doc(
          db,
          `projects/${projectId}/boards/${boardId}/columns`,
          source.droppableId
        );
        await updateDoc(startColumnRef, {
          taskIds: newInitialData.columns[source.droppableId].taskIds,
        });

        if (source.droppableId !== destination.droppableId) {
          const endColumnRef = doc(
            db,
            `projects/${projectId}/boards/${boardId}/columns`,
            destination.droppableId
          );
          await updateDoc(endColumnRef, {
            taskIds: newInitialData.columns[destination.droppableId].taskIds,
          });
        }
      } else {
        const columnOrderRef = doc(
          db,
          `projects/${projectId}/boards/${boardId}/columns`,
          "columnOrder"
        );
        await updateDoc(columnOrderRef, { order: newInitialData.columnOrder });
      }
    } catch (error) {
      console.error("Error updating Firestore:", error);
    }
  };

  const addCol = async (e) => {
    e.preventDefault();
    const newColumnName = e.target.elements.newCol.value;
    const columnsRef = collection(
      db,
      `projects/${projectId}/boards/${boardId}/columns`
    );

    try {
      const docRef = doc(columnsRef, newColumnName);
      await setDoc(docRef, {
        title: newColumnName,
        taskIds: [],
      });

      const columnOrderRef = doc(columnsRef, "columnOrder");
      const columnOrderSnap = await getDoc(columnOrderRef);

      if (!columnOrderSnap.exists()) {
        await setDoc(columnOrderRef, {
          order: [],
        });
      }

      await updateDoc(columnOrderRef, {
        order: arrayUnion(newColumnName),
      });

      e.target.elements.newCol.value = "";
    } catch (error) {
      console.error("Error adding column:", error);
    }
  };

  const changeBoardName = debounce(async (ev) => {
    try {
      await updateDoc(doc(db, `projects/${projectId}/boards`, boardId), {
        name: ev,
      });
    } catch (error) {
      console.error("Error updating board name:", error);
    }
  }, 7000);

  if (!projectId || !boardId) {
    console.error("Missing projectId or boardId");
    return null;
  }

  return (
    <>
      {initialData ? (
        <>
          <Modal modal={modal} setModal={setModal} ariaText="Add a new task">
            <AddTask
              boardId={boardId}
              userId={userId}
              projectId={projectId}
              allCols={initialData.columnOrder}
              close={() => setModal(false)}
            />
          </Modal>

          <main className="pb-2 h-screen">
            <div className="flex flex-col h-full">
              <header className="bg-white z-10 text-sm sm:text-base py-5 px-4 mb-3 rounded-lg mx-3 md:mx-6">
                <div className="flex flex-wrap justify-between items-center">
                  <span className="text-xl">
                    <Link
                      to={`/tasks`}
                      className="text-blue-800 hover:text-blue-500 font-semibold"
                    >
                      Boards{" "}
                    </Link>
                    <span className="">/</span>
                    <input
                      type="text"
                      value={boardName || ""}
                      className="text-gray-800 ml-2 w-1/2 truncate"
                      onChange={(e) => changeBoardName(e.target.value)}
                    />
                  </span>
                  <div className="flex flex-wrap items-center sm:space-x-9">
                    <div className="flex items-center mt-2 sm:mt-0">
                      <h3 className="text-gray-500 mr-2">Show Priority: </h3>
                      <div className="space-x-1 text-blue-900 flex bg-indigo-50 rounded-sm">
                        <div
                          className={`px-2 py-1 cursor-pointer hover:text-white hover:bg-blue-600 rounded-sm ${
                            filter === null ? "bg-blue-600 text-blue-50" : ""
                          }`}
                          onClick={() => setFilter(null)}
                        >
                          All
                        </div>
                        {filters.map((f) => (
                          <div
                            key={f}
                            className={`px-3 border-black py-1 hover:bg-blue-600 hover:text-blue-50 cursor-pointer capitalize ${
                              filter === f ? "bg-blue-600 text-blue-50" : ""
                            }`}
                            onClick={() => setFilter(f === "all" ? null : f)}
                          >
                            {f}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div
                      className="text-white bg-gradient-to-br from-primary via-indigo-600 to-blue-600 transform hover:scale-110 transition-all duration-300 rounded-full p-2 sm:p-1 fixed bottom-6 right-6 sm:static"
                      onClick={() => setModal(true)}
                    >
                      <Add />
                    </div>
                  </div>
                </div>
              </header>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                  droppableId="allCols"
                  type="column"
                  direction="horizontal"
                >
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="grid overflow-x-scroll overflow-y-scroll h-[85vh] w-[78vw] items-start pt-3 md:pt-2 mx-1 md:mx-6 auto-cols-220 md:auto-cols-270 grid-flow-col"
                    >
                      {initialData?.columnOrder &&
                        initialData?.columnOrder.map((col, i) => {
                          const column = initialData?.columns[col];
                          if (!column) return null;
                          const tasks = column.taskIds
                            ?.map((t) => initialData.tasks[t])
                            .filter((task) => task);
                          return (
                            <Column
                              column={column}
                              tasks={tasks || []}
                              allData={initialData}
                              key={column.id}
                              boardId={boardId}
                              userId={userId}
                              projectId={projectId}
                              filterBy={filter}
                              index={i}
                            />
                          );
                        })}
                      <form
                        onSubmit={addCol}
                        autoComplete="off"
                        className="ml-2"
                      >
                        <input
                          maxLength="20"
                          className="truncate bg-transparent border-indigo-400 placeholder-indigo-500 text-indigo-800 bg-indigo-50 px-2 py-1 rounded-sm border-[3px] focus:ring-indigo-500"
                          type="text"
                          name="newCol"
                          placeholder="Add a new List"
                        />
                      </form>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </main>
        </>
      ) : (
        <div className="spinner h-screen w-screen" />
      )}
    </>
  );
};

export default Kanban;
